import React, { useState } from 'react';

import FilledBadge from '../Badge/FilledBadge';
import BasicButton from '../Button/BasicButton';
import { ChevronDownIcon, ChevronUpIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { formatTimeRange } from '@/utils/common';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Stack } from '@mui/material';

export interface ContentBoxProps {
  id: number;
  title: string;
  badgeLabel?: string;
  badgeTextColor?: string;
  detailContents: any;
  expandContents?: ContentBoxDetail[];
  button?: string;
  buttonList?: ButtonProps[];
  buttonDisabled?: boolean;
  buttonOnClick?: (id: number) => void;
  isMore?: boolean;
  useExpand?: boolean;
  moreMenuData?: MenuType[];
}

export interface ContentBoxDetail {
  key: string;
  value: string;
}

const ContentBox = ({
  id,
  title,
  badgeLabel,
  badgeTextColor,
  detailContents,
  button,
  buttonList,
  buttonDisabled,
  buttonOnClick,
  useExpand = false,
  isMore = false,
  moreMenuData,
  expandContents,
}: ContentBoxProps) => {
  const [isShowMoreMenu, setIsShowMoreMenu] = useState<boolean>(false);
  const [isExpandDetail, setIsExpandDetail] = useState<boolean>(false);

  const onToggleMenu = () => {
    setIsShowMoreMenu(!isShowMoreMenu);
  };

  const onToggleDetail = () => {
    setIsExpandDetail(!isExpandDetail);
  };

  return (
    <>
      <ul className="grid w-full grid-cols-1 gap-3">
        <li className="p-3 px-4 border rounded-lg">
          {badgeLabel && badgeTextColor && (
            <div className="mt-1 mb-3">
              <FilledBadge
                text={badgeLabel}
                textColor={badgeTextColor}
                bgColor={'bg-gray-1'}
                rounded={'rounded-lg'}
                fontWeight={'font-semibold'}
              ></FilledBadge>
            </div>
          )}
          <div className="flex items-center pb-2">
            <div className="flex items-center justify-between flex-1">
              <p className="text-lg xxs:text-base text-grey-8">{title}</p>
              {isMore && (
                <div className="pb-2 cursor-pointer">
                  <MoreVertIcon onClick={onToggleMenu}></MoreVertIcon>
                </div>
              )}
              {useExpand && (
                <div className="pb-2 cursor-pointer" onClick={onToggleDetail}>
                  {isExpandDetail ? (
                    <ChevronUpIcon color={colors.gray[7]} />
                  ) : (
                    <ChevronDownIcon color={colors.gray[7]} />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="pb-2 text-lg font-semibold text-grey-9">
            급여 {detailContents.salary}만원({detailContents.salaryType.desc})
          </div>
          <div className="text-sm text-grey-9">
            {detailContents.workingDays.desc} ·&nbsp;
            {formatTimeRange(detailContents.workingStartHour, detailContents.workingEndHour)}
          </div>
          <div className="text-sm text-grey-7">
            {detailContents.transportItem} ·&nbsp;
            {detailContents.transportSection}
          </div>
          {isExpandDetail && expandContents && (
            <>
              <div className="p-4 my-4 rounded-lg bg-gray-1">
                {expandContents.map((item, index) => (
                  <div className={`flex items-center ${index === 0 ? 'mt-0' : 'mt-2'}`} key={index}>
                    <span className="flex-1 pr-4 text-base text-grey-7">{item.key}</span>
                    <span className="text-base text-grey-8">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                {buttonList?.map((item, index) => (
                  <BasicButton key={index} {...item}></BasicButton>
                ))}
              </div>
            </>
          )}
          {button && buttonOnClick && (
            <div className="pt-3 mt-3">
              <BasicButton
                name={button}
                bgColor={colors.primary[10]}
                borderColor={colors.primary[10]}
                textColor={colors.gray[0]}
                fontSize={16}
                height={48}
                fontWeight="bold"
                isDisabled={buttonDisabled}
                onClick={() => buttonOnClick(id)}
              ></BasicButton>
            </div>
          )}
        </li>
      </ul>
    </>
  );
};

export default ContentBox;
